import React, { useState, createElement, Component } from 'react';
import { useDebug } from './provider';
import { X, Terminal, Copy, Download, Trash2, ChevronRight, CheckCircle2, AlertCircle, Search, Check } from 'lucide-react';
const DEFAULT_SOURCE_STYLE = {
  bg: 'bg-slate-800',
  text: 'text-slate-400'
};
export function DebugPanel() {
  const {
    logs,
    isOpen,
    setIsOpen,
    clearLogs,
    config
  } = useDebug();
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'all' | 'error' | 'success'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  if (!isOpen) {
    return <div className="fixed bottom-0 right-0 w-8 h-8 z-[9999] cursor-crosshair opacity-0 hover:opacity-100 transition-opacity flex items-end justify-end p-1" onClick={() => setIsOpen(true)} title="Abrir Debug Panel (Ctrl+Shift+D)" data-id="element-6797">
        <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" data-id="element-6798" />
      </div>;
  }
  const getSourceStyle = (source: string) => {
    return config.sources?.[source] || DEFAULT_SOURCE_STYLE;
  };
  const toggleLog = (id: string) => {
    const newSet = new Set(expandedLogs);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setExpandedLogs(newSet);
  };
  const activeSources = Array.from(new Set(logs.map(l => l.source))).sort();
  const filteredLogs = logs.filter(log => {
    if (activeTab === 'error' && log.success) return false;
    if (activeTab === 'success' && !log.success) return false;
    if (sourceFilter !== 'all' && log.source !== sourceFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return log.tool.toLowerCase().includes(q) || log.source.toLowerCase().includes(q) || log.error && log.error.toLowerCase().includes(q) || JSON.stringify(log.params).toLowerCase().includes(q);
    }
    return true;
  });
  const handleCopy = async (data: any, label: string) => {
    try {
      const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      setCopiedId(label);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopiedId(label);
        setTimeout(() => setCopiedId(null), 2000);
      } catch {}
    }
  };
  const handleExportAll = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(logs, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute('download', `debug_logs_${new Date().toISOString()}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };
  return <div className="fixed inset-0 z-[9999] flex justify-end pointer-events-none" data-id="element-6799">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto" onClick={() => setIsOpen(false)} data-id="element-6800" />

      <div className="relative w-[800px] max-w-[90vw] h-full bg-[#0f111a] border-l border-slate-800 shadow-2xl flex flex-col pointer-events-auto text-slate-300 font-mono text-sm" data-id="element-6801">
        <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-[#151822]" data-id="element-6802">
          <div className="flex items-center gap-3" data-id="element-6803">
            <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg" data-id="element-6804">
              <Terminal size={18} data-id="element-6805" />
            </div>
            <div data-id="element-6806">
              <h2 className="font-bold text-slate-100 font-sans" data-id="element-6807">
                Global Debug Console
              </h2>
              <p className="text-xs text-slate-500 font-sans" data-id="element-6808">
                Interceptando llamadas API (Ctrl+Shift+D)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-6809">
            <button onClick={handleExportAll} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors" title="Exportar todo como JSON" data-id="element-6810">
              <Download size={16} data-id="element-6811" />
            </button>
            <button onClick={clearLogs} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors" title="Limpiar logs" data-id="element-6812">
              <Trash2 size={16} data-id="element-6813" />
            </button>
            <div className="w-px h-6 bg-slate-800 mx-1" data-id="element-6814" />
            <button onClick={() => setIsOpen(false)} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors" data-id="element-6815">
              <X size={20} data-id="element-6816" />
            </button>
          </div>
        </div>

        <div className="border-b border-slate-800 bg-[#11131d]" data-id="element-6817">
          <div className="p-3 pb-2" data-id="element-6818">
            <div className="relative" data-id="element-6819">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-6820" />

              <input type="text" placeholder="Buscar por tool, source, error..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-4 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 font-sans transition-all" data-id="element-6821" />

              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-500 hover:text-slate-300" data-id="element-6822">
                  <X size={12} data-id="element-6823" />
                </button>}
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 pb-3 flex-wrap" data-id="element-6824">
            <button onClick={() => setActiveTab('all')} className={`px-3 py-1 rounded-full text-xs transition-colors ${activeTab === 'all' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:bg-slate-800'}`} data-id="element-6825">
              Todos ({logs.length})
            </button>
            <button onClick={() => setActiveTab('error')} className={`px-3 py-1 rounded-full text-xs transition-colors ${activeTab === 'error' ? 'bg-red-500/20 text-red-400' : 'text-slate-500 hover:bg-slate-800'}`} data-id="element-6826">
              Errores ({logs.filter(l => !l.success).length})
            </button>
            <button onClick={() => setActiveTab('success')} className={`px-3 py-1 rounded-full text-xs transition-colors ${activeTab === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-500 hover:bg-slate-800'}`} data-id="element-6827">
              Éxitos ({logs.filter(l => l.success).length})
            </button>

            {activeSources.length > 1 && <div className="w-px h-4 bg-slate-700 mx-1" data-id="element-6828" />}

            {activeSources.length > 1 && <>
                <button onClick={() => setSourceFilter('all')} className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider transition-colors ${sourceFilter === 'all' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:bg-slate-800'}`} data-id="element-6829">
                  All
                </button>
                {activeSources.map(src => {
              const style = getSourceStyle(src);
              const isActive = sourceFilter === src;
              return <button key={src} onClick={() => setSourceFilter(isActive ? 'all' : src)} className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider transition-colors ${isActive ? `${style.bg} ${style.text}` : 'text-slate-500 hover:bg-slate-800'}`} data-id="element-6830">
                      {src} ({logs.filter(l => l.source === src).length})
                    </button>;
            })}
              </>}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar" data-id="element-6831">
          {filteredLogs.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-slate-600 font-sans" data-id="element-6832">
              <Terminal size={48} className="mb-4 opacity-20" data-id="element-6833" />
              <p data-id="element-6834">No hay logs registrados.</p>
            </div> : filteredLogs.map(log => {
          const isExpanded = expandedLogs.has(log.id);
          const time = new Date(log.timestamp).toLocaleTimeString([], {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3
          });
          return <div key={log.id} className={`border rounded-lg overflow-hidden transition-colors ${log.success ? 'border-slate-800 bg-[#151822]' : 'border-red-900/50 bg-red-950/10'}`} data-id="element-6835">
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => toggleLog(log.id)} data-id="element-6836">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-6837">
                      <ChevronRight size={16} className={`shrink-0 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} data-id="element-6838" />

                      {log.success ? <CheckCircle2 size={16} className="shrink-0 text-emerald-500" data-id="element-6839" /> : <AlertCircle size={16} className="shrink-0 text-red-500" data-id="element-6840" />}

                      <span className="text-xs text-slate-500 shrink-0" data-id="element-6841">
                        {time}
                      </span>

                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider shrink-0 ${getSourceStyle(log.source).bg} ${getSourceStyle(log.source).text}`} data-id="element-6842">
                        {log.source}
                      </span>

                      <span className="font-bold text-slate-200 truncate" data-id="element-6843">
                        {log.tool}
                      </span>

                      {log.durationMs && <span className="text-xs text-slate-600 shrink-0" data-id="element-6844">
                          {log.durationMs}ms
                        </span>}
                    </div>

                    <div className="flex items-center gap-2 shrink-0" data-id="element-6845">
                      <button onClick={e => {
                  e.stopPropagation();
                  handleCopy(log.processedData || log.rawResponse || log.error, `data-${log.id}`);
                }} className={`p-1.5 rounded transition-all ${copiedId === `data-${log.id}` ? 'text-emerald-400 bg-emerald-500/10' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700'}`} title="Copiar respuesta" data-id="element-6846">
                        {copiedId === `data-${log.id}` ? <Check size={14} data-id="element-6847" /> : <Copy size={14} data-id="element-6848" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && <div className="p-4 border-t border-slate-800/50 bg-[#0a0c13] space-y-4" data-id="element-6849">
                      {config.actions && config.actions.length > 0 && <div className="flex flex-wrap gap-2 mb-4" data-id="element-6850">
                          {config.actions.filter(action => action.visible ? action.visible(log) : true).map(action => {
                  const Icon = action.icon;
                  return <button key={action.id} onClick={() => action.handler(log)} className={`flex items-center gap-1.5 px-3 py-1.5 ${action.bgColor} ${action.hoverBgColor} ${action.color} rounded text-xs font-sans transition-colors`} data-id="element-6851">
                                  <Icon size={14} data-id="element-6852" /> {action.label}
                                </button>;
                })}
                        </div>}

                      <div data-id="element-6853">
                        <div className="flex items-center justify-between mb-1.5" data-id="element-6854">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500" data-id="element-6855">
                            Request Params
                          </span>
                          <button onClick={() => handleCopy(log.params, `params-${log.id}`)} className={`text-xs transition-colors ${copiedId === `params-${log.id}` ? 'text-emerald-400' : 'text-indigo-400 hover:text-indigo-300'}`} data-id="element-6856">
                            {copiedId === `params-${log.id}` ? '✓ Copiado' : 'Copiar'}
                          </button>
                        </div>
                        <pre className="p-3 rounded bg-[#151822] border border-slate-800 overflow-x-auto text-emerald-400/90 text-xs leading-relaxed" data-id="element-6857">
                          {JSON.stringify(log.params, null, 2) || '{}'}
                        </pre>
                      </div>

                      {!log.success && log.error && <div data-id="element-6858">
                          <div className="flex items-center justify-between mb-1.5" data-id="element-6859">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-red-500" data-id="element-6860">
                              Error Message
                            </span>
                            <button onClick={() => handleCopy(log.error, `error-${log.id}`)} className={`text-xs transition-colors ${copiedId === `error-${log.id}` ? 'text-emerald-400' : 'text-red-400 hover:text-red-300'}`} data-id="element-6861">
                              {copiedId === `error-${log.id}` ? '✓ Copiado' : 'Copiar'}
                            </button>
                          </div>
                          <pre className="p-3 rounded bg-red-950/30 border border-red-900/50 overflow-x-auto text-red-400 text-xs leading-relaxed whitespace-pre-wrap" data-id="element-6862">
                            {log.error}
                          </pre>
                        </div>}

                      {log.rawResponse && <div data-id="element-6863">
                          <div className="flex items-center justify-between mb-1.5" data-id="element-6864">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500" data-id="element-6865">
                              Raw Server Response
                            </span>
                            <button onClick={() => handleCopy(log.rawResponse, `raw-${log.id}`)} className={`text-xs transition-colors ${copiedId === `raw-${log.id}` ? 'text-emerald-400' : 'text-indigo-400 hover:text-indigo-300'}`} data-id="element-6866">
                              {copiedId === `raw-${log.id}` ? '✓ Copiado' : 'Copiar'}
                            </button>
                          </div>
                          <pre className="p-3 rounded bg-[#151822] border border-slate-800 overflow-x-auto text-amber-400/90 text-xs leading-relaxed max-h-[300px] overflow-y-auto custom-scrollbar" data-id="element-6867">
                            {JSON.stringify(log.rawResponse, null, 2)}
                          </pre>
                        </div>}

                      {log.processedData && <div data-id="element-6868">
                          <div className="flex items-center justify-between mb-1.5" data-id="element-6869">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500" data-id="element-6870">
                              Processed / Unwrapped Data
                            </span>
                            <button onClick={() => handleCopy(log.processedData, `processed-${log.id}`)} className={`text-xs transition-colors ${copiedId === `processed-${log.id}` ? 'text-emerald-400' : 'text-indigo-400 hover:text-indigo-300'}`} data-id="element-6871">
                              {copiedId === `processed-${log.id}` ? '✓ Copiado' : 'Copiar'}
                            </button>
                          </div>
                          <pre className="p-3 rounded bg-[#151822] border border-slate-800 overflow-x-auto text-cyan-400/90 text-xs leading-relaxed max-h-[300px] overflow-y-auto custom-scrollbar" data-id="element-6872">
                            {JSON.stringify(log.processedData, null, 2)}
                          </pre>
                        </div>}
                    </div>}
                </div>;
        })}
        </div>
      </div>
    </div>;
}